// @flow

import React from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { createFragmentContainer, graphql } from 'react-relay';
import get from 'lodash.get';

import { Button } from 'dibs-elements/exports/Button';
import { Spinner } from 'dibs-elements/exports/Spinner';

import SelectProject from './SelectProject';
import * as CreateProjectMutation from '../mutations/CreateProjectMutation';
import * as TagProjectMutation from '../mutations/TagProjectMutation';
import { postProjectToFA } from '../faPoster';

import type { Environment } from 'react-relay';
import type { IntlShape } from 'react-intl';
import type { Project } from '../types.flow';

import styles from './styles/TagProject.scss';
import type { NamedSelectOption } from 'dibs-elements/exports/types';

type ErrorState = 'APPLY_TAG_ERROR' | 'CREATE_PROJECT_ERROR' | 'CREATE_PROJECT_NAME_COLLISION';

type ProjectNode = {
    node: Project,
};

type TagProjectFormStatus = 'LOADING_PROJECT' | 'SELECTING_PROJECT' | 'UPDATING_TAGS';

type TagProjectProps = {
    firmId: ?string,
    intl: IntlShape,
    item: {
        serviceId: string,
        projects: Array<ProjectNode>,
    },
    onDone: Function,
    relay: {
        environment: Environment,
        refetch: Function,
    },
    source: string,
    userId: string,
    viewer: {
        id: string,
        projects: {
            edges: Array<ProjectNode>,
        },
    },
};

type TagProjectState = {
    error: ErrorState | null,
    formStatus: TagProjectFormStatus,
    nameCollision: string,
    tagAdded: boolean,
    hasInitialTags: boolean,
};

const getArrayDifference = (array1: Array<any>, array2: Array<any>) => {
    return array1.filter(arr1Item => !array2.find(arr2Item => arr1Item === arr2Item));
};

export class TagProject extends React.Component<TagProjectProps, TagProjectState> {
    constructor(props: TagProjectProps) {
        super(props);

        this.applyTagToItem = this.applyTagToItem.bind(this);
        this.getItemProjects = this.getItemProjects.bind(this);
        this.getUserProjects = this.getUserProjects.bind(this);
        this.handleDoneClick = this.handleDoneClick.bind(this);
        this.handleProjectCreation = this.handleProjectCreation.bind(this);
        this.handleProjectSelection = this.handleProjectSelection.bind(this);
        this.renderErrorMessage = this.renderErrorMessage.bind(this);
        this.renderProjectSelector = this.renderProjectSelector.bind(this);

        this.state = {
            error: null,
            formStatus: 'SELECTING_PROJECT',
            nameCollision: '',
            tagAdded: false,
            hasInitialTags: !!this.getItemProjects().length,
        };
    }

    applyTagToItem: (project: Project, action?: string) => void;
    getItemProjects: () => Array<Project>;
    getUserProjects: () => Array<Project>;
    handleDoneClick: (e: SyntheticEvent<*>) => void;
    handleProjectCreation: (newProjectName: string) => void;
    handleProjectSelection: (selectedOptions: Array<NamedSelectOption>) => void;
    renderErrorMessage: () => React$Element<any>;
    renderProjectSelector: () => React$Element<any>;

    applyTagToItem(project: Project, action?: string) {
        const { item, relay, userId } = this.props;
        const portfolioId = get(project, 'portfolio.serviceId');
        const itemId = item.serviceId;

        this.setState({ formStatus: 'UPDATING_TAGS' });

        TagProjectMutation.commit(relay.environment, {
            action,
            itemId,
            portfolioId,
            userId,
            onCompleted: () => {
                this.setState({
                    error: null,
                    formStatus: 'SELECTING_PROJECT',
                    tagAdded: action !== 'REMOVE',
                });
            },
            onError: (error: mixed) => {
                this.setState({
                    error: 'APPLY_TAG_ERROR',
                    formStatus: 'SELECTING_PROJECT',
                });
                console.warn('Could not tag item with project:', error);
            },
        });
    }

    getItemProjects() {
        return get(this.props, 'item.projects') || [];
    }

    getUserProjects() {
        const { viewer } = this.props;

        const projectEdges = get(viewer, 'projects.edges') || [];
        return projectEdges.map(({ node }) => node);
    }

    handleDoneClick(e: SyntheticEvent<*>) {
        e.preventDefault();
        this.props.onDone(this.state.tagAdded);
    }

    handleProjectCreation(newProjectName: string) {
        const { relay, source, userId, viewer } = this.props;

        this.setState({ formStatus: 'LOADING_PROJECT' });
        CreateProjectMutation.commit(relay.environment, {
            name: newProjectName,
            userId,
            viewerId: viewer.id,
            onCompleted: (response: mixed) => {
                const newProject = get(response, 'createProject.project');

                postProjectToFA({
                    action: 'CREATE',
                    firmId: get(viewer, 'user.tradeFirm.serviceId'),
                    project: newProject,
                    source,
                    userId,
                });

                this.setState({
                    error: null,
                });
                this.applyTagToItem(newProject);
            },
            onError: (error: mixed) => {
                const errors = get(error, 'source.errors') || [];
                const isProjectNameCollision = !!errors.find(
                    err => get(err, 'requestInfo.status') === 409
                );

                this.setState({
                    error: isProjectNameCollision
                        ? 'CREATE_PROJECT_NAME_COLLISION'
                        : 'CREATE_PROJECT_ERROR',
                    formStatus: 'SELECTING_PROJECT',
                    nameCollision: isProjectNameCollision ? newProjectName : '',
                });
            },
        });
    }

    handleProjectSelection(selectedOptions: Array<NamedSelectOption>) {
        const selectedOptionsIds = selectedOptions.map(o => get(o, 'value'));
        const existingTagsIds = this.getItemProjects().map(p => p.serviceId);
        const userProjects = this.getUserProjects();

        const projectAddedId = getArrayDifference(selectedOptionsIds, existingTagsIds)[0];

        if (projectAddedId) {
            const projectAdded = userProjects.find(p => p.serviceId === projectAddedId);
            if (projectAdded) {
                this.applyTagToItem(projectAdded);
            }
        } else {
            const projectRemovedId = getArrayDifference(existingTagsIds, selectedOptionsIds)[0];
            const projectRemoved = userProjects.find(p => p.serviceId === projectRemovedId);
            if (projectRemoved) {
                this.applyTagToItem(projectRemoved, 'REMOVE');
            }
        }
    }

    renderErrorMessage() {
        const { error, nameCollision } = this.state;

        switch (error) {
            case 'APPLY_TAG_ERROR':
                return (
                    <span data-tn="create-project-error-apply-tag">
                        <FormattedMessage
                            id="dc.ProjectForms.TagProject.applyTagError"
                            defaultMessage="Error. Could not update item's project tags."
                        />
                    </span>
                );
            case 'CREATE_PROJECT_NAME_COLLISION':
                return (
                    <span data-tn="create-project-error-name-collision">
                        <FormattedMessage
                            id="dc.ProjectForms.TagProject.createProjectNameCollision"
                            defaultMessage='You already have a project called "{projectName}".'
                            values={{
                                projectName: nameCollision,
                            }}
                        />
                    </span>
                );
            case 'CREATE_PROJECT_ERROR':
                return (
                    <span data-tn="create-project-error">
                        <FormattedMessage
                            id="dc.ProjectForms.TagProject.createProjectError"
                            defaultMessage="Error. Could not create project."
                        />
                    </span>
                );
            default:
                return null;
        }
    }

    renderProjectSelector() {
        const { formStatus } = this.state;

        switch (formStatus) {
            case 'LOADING_PROJECT':
                return (
                    <div className={styles.loadingGraphicContainer}>
                        <Spinner dark />
                        <span className={styles.loadingProjectLabel}>
                            <FormattedMessage
                                id="dc.ProjectForms.TagProject.creatingProject"
                                defaultMessage="Creating project"
                            />
                        </span>
                    </div>
                );
            case 'UPDATING_TAGS':
                return (
                    <div className={styles.loadingGraphicContainer}>
                        <Spinner dark />
                        <span className={styles.loadingProjectLabel}>
                            <FormattedMessage
                                id="dc.ProjectForms.TagProject.updatingTags"
                                defaultMessage="Updating Tags"
                            />
                        </span>
                    </div>
                );
            default:
                return (
                    <SelectProject
                        itemProjects={this.getItemProjects()}
                        onProjectCreation={this.handleProjectCreation}
                        onProjectSelection={this.handleProjectSelection}
                        userProjects={this.getUserProjects()}
                    />
                );
        }
    }

    render() {
        const { formStatus, hasInitialTags } = this.state;
        const canSubmit =
            formStatus === 'SELECTING_PROJECT' &&
            ((!hasInitialTags && !!this.getItemProjects().length) || hasInitialTags);

        return (
            <div data-tn="tag-project-form">
                {this.renderProjectSelector()}
                <Button
                    className={styles.submitButton}
                    dataTn="tag-project-submit-button"
                    onClick={this.handleDoneClick}
                    disabled={!canSubmit}
                >
                    <FormattedMessage
                        id="dc.ProjectForms.CreateProject.doneCta"
                        defaultMessage="Done"
                    />
                </Button>
                <div className={styles.errorContainer}>{this.renderErrorMessage()}</div>
            </div>
        );
    }
}

const Component = TagProject;
export { Component };

// have to re-assign TagProject for relay compat
TagProject = createFragmentContainer(injectIntl(TagProject), {
    viewer: graphql`
        fragment TagProject_viewer on Viewer {
            id
            projects(userIds: $userIds, first: 200) @connection(key: "TagProject_projects") {
                edges {
                    node {
                        serviceId
                        name
                        portfolio {
                            serviceId
                        }
                    }
                }
            }
            user(userId: $userId) {
                tradeFirm {
                    serviceId
                }
            }
        }
    `,
    item: graphql`
        fragment TagProject_item on Item {
            serviceId
            projects(userId: $userId) {
                serviceId
                name
            }
        }
    `,
});

export default TagProject;
